import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect, useRef } from 'react';
import { usePositionContext } from './PositionContext';
import { useAccount } from './AccountContext';
import { useAccountType } from './AccountTypeContext';
import { useAuth } from './AuthContext';
import { aiTradingService, AITradingServiceConfig } from '../services/aiTradingService';

export interface AIStrategyConfig {
  strategy: string;
  config: any;
  isRunning: boolean;
  signal: 'buy' | 'sell' | null;
  startedTime?: number | null;
  showMonitoringMessage?: boolean;
  lastSignalTime?: number | null;
  aiPositions?: string[]; // Array of position IDs created by AI
}

interface AITradingContextType {
  assetStrategies: { [symbol: string]: AIStrategyConfig };
  updateStrategy: (symbol: string, updates: Partial<AIStrategyConfig>) => void;
  startStrategy: (symbol: string) => void;
  stopStrategy: (symbol: string) => void;
  updateSignal: (symbol: string, signal: 'buy' | 'sell' | null) => void;
  updateConfig: (symbol: string, config: any) => void;
  getStrategy: (symbol: string) => AIStrategyConfig;
  isStrategyRunning: (symbol: string) => boolean;
  executeAITrade: (symbol: string, signal: 'buy' | 'sell', currentPrice: string) => void;
  closeAllAIPositions: (symbol: string) => void;
  updatePriceForAI: (symbol: string, price: string) => void;
  registerScrollCallback: (callback: () => void) => void;
  unregisterScrollCallback: () => void;
}

const AITradingContext = createContext<AITradingContextType | undefined>(undefined);

export const useAITrading = () => {
  const context = useContext(AITradingContext);
  if (context === undefined) {
    throw new Error('useAITrading must be used within an AITradingProvider');
  }
  return context;
};

interface AITradingProviderProps {
  children: ReactNode;
}

export const AITradingProvider: React.FC<AITradingProviderProps> = ({ children }) => {
  const [assetStrategies, setAssetStrategies] = useState<{ [symbol: string]: AIStrategyConfig }>({});
  const [scrollCallback, setScrollCallback] = useState<(() => void) | null>(null);
  const { addOpenPosition, closePosition, openPositions } = usePositionContext();
  const { accountBalance, updateBalance } = useAccount();
  const { accountType } = useAccountType();
  const { user } = useAuth();

  // Auto-close monitoring is now handled centrally in PositionContext for all positions (AI and manual)

  // Initialize AI trading service and check for strategies to restore
  useEffect(() => {
    const config: AITradingServiceConfig = {
      onSignal: (symbol: string, signal: 'buy' | 'sell', price: string) => {
        updateSignal(symbol, signal);
        executeAITrade(symbol, signal, price);
      },
      onPriceUpdate: (symbol: string, price: string) => {
        // Price updates are handled by the service itself
      }
    };

    aiTradingService.setConfig(config);

    // Cleanup on unmount
    return () => {
      aiTradingService.cleanup();
    };
  }, []); // Empty dependency array for initial setup


  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Ensure all AI trading is stopped and cleaned up
      Object.keys(assetStrategies).forEach(symbol => {
        if (assetStrategies[symbol].isRunning) {
          stopStrategy(symbol);
        }
      });
      aiTradingService.cleanup();
    };
  }, []);

  const updateStrategy = useCallback((symbol: string, updates: Partial<AIStrategyConfig>) => {
    setAssetStrategies(prev => {
      const currentConfig = prev[symbol] || {
        strategy: '',
        config: { period: 10, multiplier: 3.0, tradeAmountPercent: 10 },
        isRunning: false,
        signal: null,
        startedTime: null,
        showMonitoringMessage: false,
        lastSignalTime: null,
        aiPositions: []
      };

      return {
        ...prev,
        [symbol]: {
          ...currentConfig,
          ...updates
        }
      };
    });
  }, []);

  const updateSignal = useCallback((symbol: string, signal: 'buy' | 'sell' | null) => {
    updateStrategy(symbol, { 
      signal,
      lastSignalTime: signal ? Date.now() : null
    });
  }, [updateStrategy]);

  const updateConfig = useCallback((symbol: string, config: any) => {
    updateStrategy(symbol, { config });
  }, [updateStrategy]);

  // Cache for configuration to prevent repeated Firestore calls
  const configCache = useRef<{ [symbol: string]: { config: any; timestamp: number } }>({});
  const CACHE_DURATION = 10000; // 10 seconds cache

  // Function to get latest configuration from Firestore with caching
  const getLatestConfigFromFirestore = useCallback(async (symbol: string) => {
    try {
      // Early return if user is not authenticated
      if (!user?.uid) {
        console.warn(`⚠️ User not authenticated, cannot load config for ${symbol}`);
        return null;
      }

      // Check cache first
      const cached = configCache.current[symbol];
      if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
        return cached.config;
      }

      // Import the persistence service to get the latest configuration
      const { AIStrategyPersistenceService } = await import('../services/AIStrategyPersistenceService');
      
      // Get current user ID from authenticated user context
      const userId = user.uid;
      
      // console.log(`🔍 Loading strategy for ${symbol}:`, {
      //   userId,
      //   accountType,
      //   symbol,
      //   userAuthenticated: !!user
      // });
      
      const savedStrategy = await AIStrategyPersistenceService.loadActiveStrategy(
        userId, 
        accountType, 
        symbol
      );
      
      // console.log(`📋 Strategy loading result for ${symbol}:`, {
      //   found: !!savedStrategy,
      //   strategy: savedStrategy ? {
      //     id: savedStrategy.id,
      //     name: savedStrategy.name,
      //     isActive: savedStrategy.isActive,
      //     aiTradeStartEnabled: savedStrategy.aiTradeStartEnabled
      //   } : null
      // });
      
      const config = savedStrategy?.config || null;
      
      // Cache the result
      configCache.current[symbol] = {
        config,
        timestamp: Date.now()
      };
      
      return config;
    } catch (error) {
      console.warn(`⚠️ Could not load latest config for ${symbol}:`, error);
      return null;
    }
  }, [accountType, user]);

  // 🔥 CRITICAL: Periodic configuration refresh for active AI strategies (less aggressive)
  useEffect(() => {
    const refreshInterval = setInterval(async () => {
      // Only refresh configuration for running strategies that haven't been refreshed recently
      const now = Date.now();
      for (const symbol of Object.keys(assetStrategies)) {
        const strategy = assetStrategies[symbol];
        if (strategy.isRunning) {
          try {
            // Check if we need to refresh (avoid too frequent calls)
            const cached = configCache.current[symbol];
            if (cached && (now - cached.timestamp) < CACHE_DURATION) {
              continue; // Skip if recently cached
            }

            const latestConfig = await getLatestConfigFromFirestore(symbol);
            if (latestConfig) {
              // Check if configuration has changed
              if (JSON.stringify(strategy.config) !== JSON.stringify(latestConfig)) {
                // console.log(`🔄 Configuration updated for running strategy ${symbol}:`, latestConfig);
                updateStrategy(symbol, { config: latestConfig });
                
                // Update AI trading service with new configuration
                aiTradingService.addSymbol(symbol, strategy.strategy, latestConfig);
              }
            }
          } catch (error) {
            console.warn(`⚠️ Could not refresh config for ${symbol}:`, error);
          }
        }
      }
    }, 60000); // Refresh every 60 seconds (less aggressive)

    return () => {
      clearInterval(refreshInterval);
    };
  }, [assetStrategies, getLatestConfigFromFirestore, updateStrategy]);

  const getStrategy = useCallback((symbol: string): AIStrategyConfig => {
    return assetStrategies[symbol] || {
      strategy: 'supertrend', // Default to LazyB CrazyB strategy
      config: { period: 10, multiplier: 3.0, tradeAmountPercent: 10 },
      isRunning: false,
      signal: null,
      startedTime: null,
      showMonitoringMessage: false,
      lastSignalTime: null,
      aiPositions: []
    };
  }, [assetStrategies]);

  const isStrategyRunning = useCallback((symbol: string): boolean => {
    return getStrategy(symbol).isRunning;
  }, [getStrategy]);

  // 🔥 CRITICAL FIX: Function to update Firestore isActive flag
  const updateFirestoreIsActive = useCallback(async (symbol: string, isActive: boolean) => {
    try {
      if (!user?.uid) {
        console.warn(`⚠️ User not authenticated, cannot update Firestore for ${symbol}`);
        return;
      }

      // Import the persistence service to update the isActive flag
      const { AIStrategyPersistenceService } = await import('../services/AIStrategyPersistenceService');
      
      // Get current user ID from authenticated user context
      const userId = user.uid;
      
      // console.log(`🔥 Updating Firestore isActive flag for ${symbol}:`, {
      //   userId,
      //   accountType,
      //   symbol,
      //   isActive,
      //   userAuthenticated: !!user
      // });
      
      // Update the isActive flag using the persistence service
      await AIStrategyPersistenceService.setAssetAITradingEnabled(
        userId, 
        accountType, 
        symbol, 
        isActive
      );
      
      // console.log(`✅ Successfully updated Firestore isActive flag for ${symbol}:`, isActive);
    } catch (error) {
      console.error(`❌ Error updating Firestore isActive flag for ${symbol}:`, error);
    }
  }, [user, accountType]);

  // Track starting strategies to prevent multiple simultaneous starts
  const startingStrategies = useRef<Set<string>>(new Set());

  const stopStrategy = useCallback(async (symbol: string) => {
    // Close all AI positions for this symbol before stopping
    const strategy = assetStrategies[symbol];
    if (strategy?.aiPositions) {
      strategy.aiPositions.forEach(positionId => {
        const position = openPositions.find(p => p.id === positionId);
        if (position) {
          closePosition(positionId, position.current);
        }
      });
    }

    // Remove symbol from global AI trading service
    aiTradingService.removeSymbol(symbol);

    updateStrategy(symbol, {
      isRunning: false,
      startedTime: null,
      showMonitoringMessage: false,
      signal: null,
      lastSignalTime: null,
      aiPositions: []
    });

    // 🔥 CRITICAL FIX: Update Firestore isActive flag to false
    await updateFirestoreIsActive(symbol, false);
  }, [updateStrategy, assetStrategies, openPositions, closePosition, updateFirestoreIsActive]);

  const startStrategy = useCallback(async (symbol: string) => {
    const strategy = getStrategy(symbol);
    
    // Don't start if no strategy is selected
    if (!strategy.strategy) {
      return;
    }

    // Prevent multiple simultaneous starts for the same symbol
    if (startingStrategies.current.has(symbol)) {
      // console.log(`⏸️ Strategy start already in progress for ${symbol}, skipping...`);
      return;
    }

    // Don't start if already running
    if (strategy.isRunning) {
      // console.log(`⏸️ Strategy already running for ${symbol}, skipping...`);
      return;
    }

    startingStrategies.current.add(symbol);
    
    try {
      // console.log(`🔄 Starting AI trading for ${symbol} - refreshing configuration from Firestore...`);
      
      // Get the latest configuration from Firestore
      const latestConfig = await getLatestConfigFromFirestore(symbol);
      
      if (latestConfig) {
        // console.log(`📥 Latest configuration loaded for ${symbol}:`, latestConfig);
        
        // Update the strategy with the latest configuration
        updateStrategy(symbol, {
          strategy: strategy.strategy, // Keep current strategy type
          config: latestConfig,
          isRunning: true,
          signal: null,
          startedTime: Date.now(),
          showMonitoringMessage: false,
          lastSignalTime: null,
          aiPositions: []
        });
        
        // Use the latest configuration for AI trading service
        aiTradingService.addSymbol(symbol, strategy.strategy, latestConfig);
        
        // 🔥 CRITICAL FIX: Update Firestore isActive flag
        await updateFirestoreIsActive(symbol, true);
      } else {
        // console.log(`⚠️ No saved configuration found for ${symbol}, using current config`);
        
        updateStrategy(symbol, {
          isRunning: true,
          signal: null,
          startedTime: Date.now(),
          showMonitoringMessage: false,
          lastSignalTime: null,
          aiPositions: []
        });

        // Add symbol to global AI trading service with current config
        aiTradingService.addSymbol(symbol, strategy.strategy, strategy.config);
        
        // 🔥 CRITICAL FIX: Update Firestore isActive flag
        await updateFirestoreIsActive(symbol, true);
      }

      // Show monitoring message after 2 seconds
      setTimeout(() => {
        updateStrategy(symbol, { showMonitoringMessage: true });
      }, 2000);
    } catch (error) {
      console.error(`❌ Error refreshing configuration for ${symbol}:`, error);
      
      // Fallback to current configuration if refresh fails
      updateStrategy(symbol, {
        isRunning: true,
        signal: null,
        startedTime: Date.now(),
        showMonitoringMessage: false,
        lastSignalTime: null,
        aiPositions: []
      });

      aiTradingService.addSymbol(symbol, strategy.strategy, strategy.config);
    } finally {
      // Always remove from starting set
      startingStrategies.current.delete(symbol);
    }
  }, [updateStrategy, getStrategy, getLatestConfigFromFirestore]);

  // DISABLED: Listen for auto-start events from BackendAITradingContext
  // This was causing AI to restart after manual stop
  useEffect(() => {
    const handleAutoStartStrategy = (event: CustomEvent) => {
      // Check if manual stop flag is set - if so, don't auto-start
      const hasManualStopFlag = localStorage.getItem('aiManualStop') === 'true';
      
      if (hasManualStopFlag) {
        // console.log('⏸️ Auto-start blocked: Manual stop detected');
        return;
      }
      
      const { symbol, strategy, config } = event.detail;
      
      // Set the strategy and config for the symbol
      updateStrategy(symbol, { 
        strategy: strategy,
        config: config || { period: 10, multiplier: 3.0, tradeAmountPercent: 10 }
      });
      
      // Start the strategy
      setTimeout(() => {
        startStrategy(symbol);
      }, 1000); // Small delay to ensure state is updated
    };

    window.addEventListener('autoStartAIStrategy', handleAutoStartStrategy as EventListener);

    return () => {
      window.removeEventListener('autoStartAIStrategy', handleAutoStartStrategy as EventListener);
    };
  }, [updateStrategy, startStrategy]);

  // Execute AI trade based on signal - ONE POSITION AT A TIME
  const executeAITrade = useCallback(async (symbol: string, signal: 'buy' | 'sell', currentPrice: string) => {
    if (!currentPrice || !accountBalance) {
      return;
    }

    const strategy = getStrategy(symbol);
    if (!strategy.isRunning) {
      return;
    }

    // 🔥 CRITICAL: Refresh configuration from Firestore before executing trade (with caching)
    let latestConfig = strategy.config;
    try {
      // Only refresh if not recently cached to avoid excessive calls
      const cached = configCache.current[symbol];
      const now = Date.now();
      
      if (!cached || (now - cached.timestamp) >= CACHE_DURATION) {
        const refreshedConfig = await getLatestConfigFromFirestore(symbol);
        if (refreshedConfig) {
          latestConfig = refreshedConfig;
          // console.log(`🔄 Refreshed configuration for trade execution on ${symbol}:`, latestConfig);
          
          // Update the strategy with latest config
          updateStrategy(symbol, { config: latestConfig });
        }
      } else {
        // Use cached config
        latestConfig = cached.config || strategy.config;
      }
    } catch (error) {
      console.warn(`⚠️ Could not refresh config for ${symbol}, using current config:`, error);
    }

    const price = parseFloat(currentPrice);
    const leverage = 100; // Always use 100x leverage
    
    // Get quantity from latest strategy configuration, fallback to calculated amount
    const strategyQuantity = latestConfig?.quantity;
    let amount: number;
    
    if (strategyQuantity && strategyQuantity > 0) {
      // Use dynamic quantity from latest strategy configuration
      amount = strategyQuantity;
      // console.log(`🎯 Using latest dynamic quantity from strategy config: ${amount} for ${symbol}`);
    } else {
      // Fallback to calculated amount based on balance percentage
      const currentBalance = accountType === 'demo' ? accountBalance.demoBalance : accountBalance.liveBalance;
      const tradeAmountUSD = currentBalance * 0.20; // 20% of balance
      amount = Math.max(1.10, tradeAmountUSD / price); // Minimum 1.10 quantity
      // console.log(`📊 Using calculated quantity: ${amount} for ${symbol} (no strategy quantity configured)`);
    }
    
    // Check if we have sufficient balance for the position
    const currentBalance = accountType === 'demo' ? accountBalance.demoBalance : accountBalance.liveBalance;
    const positionValue = amount * price;
    const marginRequired = positionValue / leverage;
    
    if (marginRequired > currentBalance) {
      // console.log(`❌ Insufficient balance for ${symbol} trade. Required: $${marginRequired.toFixed(2)}, Available: $${currentBalance.toFixed(2)}`);
      return;
    }

    // CRITICAL: Check for ANY existing AI positions for this symbol
    const existingAIPositions = openPositions.filter(position => 
      position.isAI && position.symbol === symbol
    );

    // If there's already an AI position for this symbol, DO NOT take new trades
    // Wait for the position to hit profit/loss target (handled by PositionContext auto-close)
    if (existingAIPositions.length > 0) {
      // console.log(`⏸️ SuperTrend: ${symbol} already has an active AI position - waiting for profit/loss target`);
      // console.log(`📊 Current position: ${existingAIPositions[0].type} at $${existingAIPositions[0].entry}`);
      // console.log(`💰 Current P&L: ${existingAIPositions[0].pnl} (${existingAIPositions[0].pnlPercent})`);
      // console.log(`🎯 Waiting for profit >= $3.99 or loss >= $1.99 before next trade`);
      return; // Exit without taking new trade
    }

    // Create new AI position - ONLY ONE POSITION AT A TIME
    if (addOpenPosition) {
      // console.log(`🚀 Opening new ${signal.toUpperCase()} position for ${symbol} at $${price}`);
      
      const positionData = {
        symbol: symbol,
        type: (signal === 'buy' ? 'Buy' : 'Sell') as 'Buy' | 'Sell',
        size: amount.toFixed(6), // More precision for crypto amounts
        entry: price.toString(),
        current: price.toString(),
        pnl: '$0.00',
        pnlPercent: '0.00%',
        leverage: leverage,
        orderType: 'market' as const, // AI trades are always market orders
        isAI: true, // Mark as AI position
      };
      
      const newPosition = await addOpenPosition(positionData);
      
      if (newPosition) {
        // console.log(`✅ Successfully opened ${signal.toUpperCase()} position for ${symbol} (ID: ${newPosition.id})`);
      } else {
        // console.log(`❌ Failed to open ${signal.toUpperCase()} position for ${symbol}`);
        return;
      }
      
      // Deduct margin from account balance for AI trades (same as manual trades)
      if (updateBalance && newPosition) {
        const positionValue = amount * price;
        const marginRequired = positionValue / leverage; // Correct margin calculation
        
        // Use function approach to get the most current balance
        await updateBalance(accountType, (currentBalance: number) => {
          const newBalance = currentBalance - marginRequired;
          return newBalance;
        });
      }
      
      // Track the new AI position ID - ONLY ONE POSITION AT A TIME
      if (newPosition) {
        // Replace any existing positions with the new one (should be empty after closing)
        updateStrategy(symbol, { 
          aiPositions: [newPosition.id] // Only track one position
        });
        
        // Clear the signal after successful trade execution
        updateStrategy(symbol, { 
          signal: null,
          lastSignalTime: null
        });
        
        // console.log(`📊 AI Position tracking updated for ${symbol}: [${newPosition.id}]`);
        
        // Call scroll callback if registered
        if (scrollCallback) {
          scrollCallback();
        }
      }
    }
  }, [accountBalance, accountType, getStrategy, addOpenPosition, closePosition, openPositions, updateStrategy, updateBalance, getLatestConfigFromFirestore]);

  // Update AI service config when functions change
  useEffect(() => {
    const config: AITradingServiceConfig = {
      onSignal: (symbol: string, signal: 'buy' | 'sell', price: string) => {
        updateSignal(symbol, signal);
        executeAITrade(symbol, signal, price);
      },
      onPriceUpdate: (symbol: string, price: string) => {
        // Price updates are handled by the service itself
      }
    };

    aiTradingService.setConfig(config);
  }, [updateSignal, executeAITrade]);

  // Close all AI positions for a specific symbol
  const closeAllAIPositions = useCallback((symbol: string) => {
    const strategy = getStrategy(symbol);
    if (strategy.aiPositions) {
      strategy.aiPositions.forEach(positionId => {
        const position = openPositions.find(p => p.id === positionId);
        if (position) {
          closePosition(positionId, position.current);
        }
      });
    }
    updateStrategy(symbol, { aiPositions: [] });
  }, [getStrategy, openPositions, closePosition, updateStrategy]);

  // Update price data for AI trading service
  const updatePriceForAI = useCallback((symbol: string, price: string) => {
    // Only update price if AI trading is active for this symbol
    if (aiTradingService.isSymbolActive(symbol)) {
      aiTradingService.updateSymbolPrice(symbol, price);
    }
  }, []);

  // Track AI positions by checking openPositions for AI-flagged positions
  useEffect(() => {
    Object.keys(assetStrategies).forEach(symbol => {
      const strategy = assetStrategies[symbol];
      if (strategy.isRunning) {
        const aiPositions = openPositions
          .filter(p => p.symbol === symbol && p.isAI)
          .map(p => p.id);

        // The strategy re-enters on its own once it is flat, so it needs to know when a
        // position closes in order to start its cooldown from that moment.
        aiTradingService.setPositionState(symbol, aiPositions.length > 0);

        if (JSON.stringify(aiPositions) !== JSON.stringify(strategy.aiPositions)) {
          updateStrategy(symbol, { aiPositions });
        }
      }
    });
  }, [openPositions, assetStrategies, updateStrategy]);

  // Scroll callback functions
  const registerScrollCallback = useCallback((callback: () => void) => {
    setScrollCallback(() => callback);
  }, []);

  const unregisterScrollCallback = useCallback(() => {
    setScrollCallback(null);
  }, []);

  const value: AITradingContextType = {
    assetStrategies,
    updateStrategy,
    startStrategy,
    stopStrategy,
    updateSignal,
    updateConfig,
    getStrategy,
    isStrategyRunning,
    executeAITrade,
    closeAllAIPositions,
    updatePriceForAI,
    registerScrollCallback,
    unregisterScrollCallback
  };

  return (
    <AITradingContext.Provider value={value}>
      {children}
    </AITradingContext.Provider>
  );
};
